import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface NavigationOptions {
	state?: Record<string, unknown>;
	replace?: boolean;
}

export function useNavigation() {
	const navigate = useNavigate();
	const location = useLocation();

	return {
		// Path-based navigation
		navigateTo: useCallback(
			(path: string, options?: NavigationOptions) => {
				navigate(path, options);
			},
			[navigate],
		),

		// Get current path
		getCurrentPath: useCallback(() => location.pathname, [location]),

		// Standard navigation
		goBack: useCallback(() => {
			navigate(-1);
		}, [navigate]),

		canGoBack: useCallback(
			() =>
				// In web browsers, we can't reliably check if we can go back
				// Return true to allow the UI to show back buttons
				true,
			[],
		),

		// Replace current screen
		replaceTo: useCallback(
			(path: string) => {
				navigate(path, { replace: true });
			},
			[navigate],
		),

		// Direct navigation (for compatibility)
		navigate: useCallback(
			(screenName: string, params?: unknown) => {
				// For web, screenName is typically a path
				if (params != null) {
					// Encode params in the path or state
					navigate(screenName, { state: params as Record<string, unknown> });
				} else {
					navigate(screenName);
				}
			},
			[navigate],
		),

		// Get location state (for passing forward on detail page navigation)
		getLocationState: useCallback(
			() => location.state as Record<string, unknown> | null,
			[location.state],
		),

		// Navigate to a list item detail page
		navigateToListItem: useCallback(
			(baseUrl: string, itemId: string | number, listKey: string) => {
				const detailPath = `${baseUrl}/${itemId}`;
				navigate(detailPath, {
					state: {
						fromList: listKey,
						returnPath: location.pathname + location.search,
					},
				});
			},
			[navigate, location.pathname, location.search],
		),
	};
}
